"use client";

import { cn } from "@/lib/utils";
import React, { useEffect, useState } from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "../ui/badge";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Progress } from "@/components/ui/progress";

interface Feature17Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  removePaddingBottom?: boolean;
  themeChangeOnScroll?: boolean;
  badge?: string;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  secondaryButton?: boolean;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  secondaryButtonStyle?: ButtonStyleType;
  items?: {
    title?: string;
    content?: string;
    link?: string;
  }[];
  attributes?: React.HTMLProps<HTMLElement>;
}



const Feature17 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 17",
  removePaddingBottom = false,
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  secondaryButton,
  secondaryButtonText,
  secondaryButtonLink,
  secondaryButtonStyle,
  items,
  attributes,
}: Feature17Props) => {

  const ItemBlock = ({item}: {item: {title?: string, content?: string, link?: string}}) => {
    return (
      item.link ? (
        <a href={item.link ?? ""} className={cn("rounded-2xl border border-border hover:border-brand flex-1 h-full flex")}>
          <div className="flex flex-col gap-20 bg-background rounded-2xl p-6 flex-1 h-full justify-between">
            <div className="flex items-center justify-between gap-10">
              {item.title && (
                <span className="paragraph-lg font-bold">{parse(item.title)}</span>
              )}
              <Button variant="outline" size="iconLg" grayBorder lifted className="hidden lg:block">
                <span className="material-symbols-outlined">
                  arrow_forward
                </span>
              </Button>
            </div>
            <div className="flex flex-col gap-6">
              {item.content && (
                <div className="text-muted-foreground">
                  {parse(item.content)}
                </div>
              )}
              <Button variant="outline" size="iconLg" grayBorder lifted className="block lg:hidden">
                <span className="material-symbols-outlined">
                  arrow_forward
                </span>
              </Button>
            </div>
          </div>
        </a>
      ) : (
        <div className="flex flex-col gap-20 bg-background border border-border rounded-xl p-6 flex-1 h-full justify-between">
          <div className="flex items-center justify-between gap-10">
            {item.title && (
              <span className="paragraph-lg font-bold">{parse(item.title)}</span>
            )}
          </div>
          <div className="flex flex-col gap-6">
            {item.content && (
              <div className="text-muted-foreground">
                {parse(item.content)}
              </div>
            )}
          </div>
        </div>
      )
    );  
  }

  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [progress, setProgress] = useState(Math.floor(100 / (items?.length ?? 1)));

  useEffect(() => {
    if (!carouselApi) {
      return;
    }
    carouselApi.on("scroll", ({ scrollProgress }: { scrollProgress: () => number }) => {
      setProgress(
        Math.max(1 / (items?.length ?? 1), Math.min(1, scrollProgress())) * 100,
      );
    });
  }, [carouselApi, items?.length]);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      removePaddingBottom={removePaddingBottom}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature17"
      attributes={attributes}
    >

      <div className="container">
        <div className="flex flex-col justify-between gap-6 text-center md:text-left" data-aos="fade-up">
          {title && (
            <> 
              {badge && (
                <Badge variant="outline" bold>
                  {badge}
                </Badge>
              )}
              <h2>
                {parse(title)}
              </h2>
            </>
          )}
          {(content || hasButton) && (
            <>  
              {content && (
                <div className="text-muted-foreground">
                  {parse(content)}
                </div>
              )}
              {hasButton && (
                <div className="flex flex-col md:flex-row gap-4 mt-4">
                  <div className="flex flex-col">
                    <Button asChild variant={buttonStyle} size="lg" className="w-full sm:w-auto">
                      <Link href={buttonLink ?? ""}>
                        {buttonText}
                      </Link>
                    </Button>
                    {microCopy && (
                      <p className="paragraph-sm text-muted-foreground text-center md:text-left mt-3">
                        {microCopy}
                      </p>
                    )}
                  </div>
                  {secondaryButton && (
                    <Button asChild variant={secondaryButtonStyle} size="lg" className="w-full sm:w-auto">
                      <Link href={secondaryButtonLink ?? ""}>
                        {secondaryButtonText}
                      </Link>
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="mt-14 lg:mt-20 gap-6 md:grid-cols-2 hidden lg:grid" data-aos="fade-up" data-aos-delay={100}>
          {items && items?.slice(0, 2).map((item, index) => (  
            <ItemBlock key={index} item={item} />
          ))}

         {items && items?.length > 2 && (
            <div className="grid gap-6 md:col-span-2 md:grid-cols-3">
              {items?.slice(2, 5).map((item, index) => (
                <ItemBlock key={index} item={item} />
              ))}
            </div>
          )}
        </div>

        
      </div>
      <div className="relative w-full overflow-hidden mt-14 lg:mt-20 block lg:hidden" data-aos="fade-up" data-aos-delay={100}>
        <Carousel 
          setApi={setCarouselApi}
          opts={{
            align: "start",
            breakpoints: {
              "(max-width: 768px)": {
                dragFree: true,
              },
            },
          }}
          data-aos="fade-left"
        >
          <CarouselContent 
            className={cn(
              "mr-4 ml-0",
              "md:mr-8 md:ml-3",
              "2xl:mr-[max(0rem,calc(100vw/2-1336px/2))] 2xl:ml-[max(0rem,calc(100vw/2-1336px/2-20px))]"
            )}
          >
            {items?.map((item, index) => (
              <CarouselItem 
                key={index} 
                className="pl-4 md:pl-5 max-w-[344px] grid-cols-1"
              >
                <div className="flex flex-1 h-full p-[1px]">
                  <ItemBlock item={item} />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="container mt-6">
            <Progress value={progress} className="h-[2px] w-full max-w-[300px]" />
          </div>
        </Carousel>
      </div>
    </SectionWrapper>
  )
};

export { Feature17 };